import { Link, useLocation } from "react-router-dom";
import { Home as HomeIcon, ShoppingBag, ShoppingCart, ChefHat, CalendarDays, UserRound } from "lucide-react";
import { useHouseBrand } from "@/hooks/useHouseBrand";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/lib/auth";

const linkBase =
  "shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full transition-colors";

/**
 * Shared public storefront header (logo + Home/Shop/About/Blog/Events/Cart/Account).
 * Stays scoped to the dealer whose page you're on (derived from /d/:slug/...),
 * so navigating never bounces you back to the main OreVital site.
 */
export default function PublicHeader({ active = "", nextPath }) {
  const brand = useHouseBrand();
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const m = pathname.match(/\/d\/([^/?#]+)/);
  const slug = m ? m[1] : new URLSearchParams(search).get("d") || "orevital";
  const dq = slug && slug !== "orevital" ? `?d=${slug}` : "";
  const homeHref = `/d/${slug}?view=home`;
  const shopHref = `/d/${slug}?view=shop`;
  const eventsHref = `/d/${slug}/events`;
  const blogHref = `/blog${dq}`;
  const back = nextPath || eventsHref;
  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-zinc-200">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-2">
        <Link to={homeHref} className="flex items-center shrink-0">
          <BrandLogo alt={brand.company} />
        </Link>
        <nav className="flex items-center gap-1 sm:gap-2 overflow-x-auto min-w-0 flex-1 sm:justify-end">
          <Link to={homeHref} className={`${linkBase} text-zinc-700 hover:bg-zinc-100`} data-testid="ph-nav-home">
            <HomeIcon size={14} />
            <span className="hidden sm:inline">Home</span>
          </Link>
          <Link to={shopHref} className={`${linkBase} bg-emerald-50 text-emerald-700 hover:bg-emerald-100`} data-testid="ph-nav-shop">
            <ShoppingBag size={14} />
            Shop
          </Link>
          <Link to={blogHref} className={`${linkBase} text-zinc-700 hover:bg-zinc-100 hidden sm:inline-flex`} data-testid="ph-nav-blog">
            <ChefHat size={14} />
            <span>Blog</span>
          </Link>
          <Link
            to={eventsHref}
            className={`${linkBase} ${active === "events" ? "bg-violet-600 text-white shadow-sm" : "text-emerald-700 hover:bg-emerald-50"}`}
            data-testid="ph-nav-events"
          >
            <CalendarDays size={14} />
            Events
          </Link>
          <Link to={shopHref} className="shrink-0 h-10 w-10 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 flex items-center justify-center" data-testid="ph-nav-cart" aria-label="Shop">
            <ShoppingCart size={16} />
          </Link>
        </nav>
        <Link
          to={user ? "/app/earnings" : `/login?next=${encodeURIComponent(back)}`}
          className="shrink-0 h-10 w-10 rounded-full bg-violet-50 hover:bg-violet-100 text-violet-700 flex items-center justify-center"
          data-testid="ph-nav-account"
          aria-label={user ? "Account" : "Sign in"}
          title={user ? "Account" : "Sign in"}
        >
          <UserRound size={16} />
        </Link>
      </div>
    </header>
  );
}
